import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Globe, LockKeyhole, NotebookPen } from "lucide-react";
import styles from "@/components/library/LibraryVisual.module.css";
import { Button } from "@/components/ax/Button";
import { EmptyAxis } from "@/components/ax/EmptyAxis";
import { BookCard } from "@/components/library/BookCover";
import { useFirstView } from "@/hooks/useFirstView";
import { useI18n } from "@/i18n";
import { getCatalog } from "@/lib/libraryStore";

const SOURCES_LINK_COPY = {
  id: "Sumber terbuka",
  en: "Open sources",
};

const Skeleton = () => (
  <div aria-busy="true" data-testid="catalog-skeleton" className={styles.skeleton}>
    {[0, 1, 2].map((i) => (
      <div key={i} className={styles.skeletonCard}>
        <div className={styles.skeletonCover} />
        <div className={styles.skeletonText}><div /><div /><div /></div>
      </div>
    ))}
  </div>
);

export default function Library() {
  const { t, lang } = useI18n();
  const first = useFirstView("library");
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setState("loading");
    const res = await getCatalog();
    if (res.error) return setState("error");
    setData(res);
    setState("ready");
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const last = data && Object.entries(data.progress).sort((a, b) => b[1].updatedAt.localeCompare(a[1].updatedAt))[0];
  const lastBook = last && data.books.find((b) => b.id === last[0]);
  const lastChapter = lastBook?.chapters.find((c) => c.id === last[1].chapterId);

  return (
    <div data-testid="library-page" className={styles.page}>
      <header className={styles.heading}>
        <div>
          <p className={styles.eyebrow}>{t("lib.archive")}</p>
          <h1 className={styles.title}>{t("lib.title")}</h1>
          <p className={styles.lede}>{t("lib.collectionLede")}</p>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          <Link
            to="/library/sumber"
            data-testid="sources-link"
            className={styles.notes}
          >
            <Globe size={16} strokeWidth={1.5} aria-hidden="true" />{SOURCES_LINK_COPY[lang === "en" ? "en" : "id"]}
          </Link>
          <Link
            to="/library/catatan"
            data-testid="notes-link"
            className={styles.notes}
          >
            <NotebookPen size={16} strokeWidth={1.5} aria-hidden="true" />{t("lib.notesLink")}
          </Link>
        </div>
      </header>

      {state === "loading" && <Skeleton />}

      {state === "error" && (
        <div role="alert" data-testid="library-error" className={styles.error}>
          <p className="m-0 text-base text-ax-text-2">{t("lib.failLoad")}</p>
          <Button onClick={load} data-testid="library-retry-button" className="mt-4">{t("retry")}</Button>
        </div>
      )}

      {state === "ready" && data.locked && (
        <div data-testid="library-locked" className={styles.locked}>
          <LockKeyhole size={20} strokeWidth={1.5} aria-hidden="true" /><p>{t("lib.locked")}</p>
        </div>
      )}

      {state === "ready" && data.books.length === 0 && (
        <section data-testid="library-empty" className={styles.empty}>
          <EmptyAxis play={first} />
          <p className="m-0 mt-4 text-base text-ax-text">{t("lib.empty")}</p>
        </section>
      )}

      {state === "ready" && lastBook && lastChapter && (
        <Link
          to={data.locked ? `/library/${lastBook.id}` : `/library/${lastBook.id}/bab/${lastChapter.id}`}
          data-testid="continue-reading-card"
          className={`${first ? "enter " : ""}${styles.continue}`}
        >
          <div>
            <p className="caption m-0 text-ax-muted">{t(data.locked ? "lib.lastRead" : "lib.continue")}</p>
            <p className="m-0 mt-1 text-base font-medium leading-6 text-ax-text">{lastBook.title?.[lang] ?? lastBook.id}</p>
            <p className="m-0 text-sm leading-5 text-ax-text-2">{lastChapter.title?.[lang] ?? lastChapter.id}</p>
          </div>
          <ArrowRight size={20} strokeWidth={1.5} aria-hidden="true" />
        </Link>
      )}

      {state === "ready" && data.books.length > 0 && (
        <section aria-labelledby="catalog-title">
          <div className={styles.catalogHeader}>
            <h2 id="catalog-title" className={styles.catalogTitle}>{t("lib.collection")}</h2>
            <span className={styles.count} data-testid="catalog-count">{t("lib.bookCount", { n: data.books.length })}</span>
          </div>
          <ul data-testid="catalog-books" className={`${first ? "enter " : ""}${styles.cards}`}>
            {data.books.map((b) => (
              <li key={b.id}>
                <BookCard book={b} locked={data.locked} compact />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
